/**
 * AllAdminsSection — جدول حسابات المشرفين والموظفين (موحّد لجميع المنصات).
 * بحث + فلتر حسب المسمى الوظيفي (الرول) + عرض المسمى الوظيفي (للقراءة فقط).
 *
 * iter-218f (Feb 2026) — Editing the job title has been MOVED to the
 * Supervisor Records page (فتح السجل ثم "عرض") per the user's request.
 * This page now displays the role as a read-only badge only.
 */
import { useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Users, Loader2, User, Key, Ban, RotateCcw, Archive,
  Search, Briefcase,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { getAuthHeaders } from '../../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function RoleBadge({ admin, language }) {
  // iter-218f (Feb 2026) — Edit capability removed at user's request.
  // The badge now renders the current job title as a static pill.
  const roleLabel = language === 'ar'
    ? (admin.job_title_ar || admin.job_title)
    : (admin.job_title || admin.job_title_ar);

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${roleLabel
        ? 'bg-[#d4af37]/10 border-[#d4af37]/30 text-[#d4af37]'
        : 'bg-amber-500/10 border-amber-500/30 text-amber-400'}`}
      data-testid={`admin-role-label-${admin.id}`}
    >
      <Briefcase className="h-3 w-3" />
      {roleLabel || (language === 'ar' ? 'بدون مسمى وظيفي' : 'No job title')}
    </span>
  );
}

function AdminRow({ admin, language, sendAdminResetLink, toggleBanAdmin, deleteAdmin }) {
  // Two-step archive: archive button is disabled until the account is banned/suspended
  const canArchive = admin.status === 'banned' || admin.status === 'suspended';
  // Password reset is blocked while the account is banned/suspended (matches backend guard)
  const isBlocked = admin.status === 'banned' || admin.status === 'suspended';
  return (
    <div className="bg-[#0a0a0a] rounded-xl p-4 border border-[#2a2a4e] flex items-center justify-between gap-3 flex-wrap" data-testid={`admin-row-${admin.id}`}>
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[#c9a227]/15">
          <User className="h-6 w-6 text-[#c9a227]" />
        </div>
        <div>
          <p className="font-semibold text-white">{admin.name || (language === 'ar' ? 'بدون اسم' : 'No name')}</p>
          <p className="text-sm text-[#808080]">{admin.email}</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {admin.username && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-[#A1A1AA] font-mono" dir="ltr">
                @{admin.username}
              </span>
            )}
            {admin.employee_id && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#d4af37]/15 text-[#d4af37]" dir="ltr">
                #{admin.employee_id}
              </span>
            )}
            <span className={`text-xs px-2 py-0.5 rounded-full ${admin.status === 'active'
              ? 'bg-emerald-500/20 text-emerald-500'
              : admin.status === 'pending_activation'
                ? 'bg-amber-500/20 text-amber-400'
                : 'bg-red-500/20 text-red-500'}`}>
              {admin.status === 'active'
                ? (language === 'ar' ? 'نشط' : 'Active')
                : admin.status === 'pending_activation'
                  ? (language === 'ar' ? 'بانتظار التفعيل' : 'Pending')
                  : (language === 'ar' ? 'معلق' : 'Suspended')}
            </span>
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 ms-auto flex-wrap">
        <div className="flex flex-col items-end gap-1">
          <RoleBadge admin={admin} language={language} />
          {admin.phone && (
            <p className="text-xs text-[#808080]" dir="ltr">{admin.phone}</p>
          )}
        </div>

        <div className="flex items-center gap-1.5" data-testid={`admin-actions-${admin.id}`}>
          <Button
            size="sm" variant="outline"
            onClick={() => !isBlocked && sendAdminResetLink(admin)}
            disabled={isBlocked}
            className={`h-8 w-8 p-0 ${isBlocked
              ? 'border-white/10 text-[#606060] cursor-not-allowed opacity-50'
              : 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10'}`}
            title={isBlocked
              ? (language === 'ar' ? 'ألغِ الحظر أولاً قبل إعادة تعيين كلمة المرور' : 'Unban the account first to reset password')
              : (language === 'ar' ? 'إرسال رابط إعادة تعيين كلمة المرور' : 'Send reset link')}
            data-testid={`admin-reset-${admin.id}`}
          >
            <Key className="h-3.5 w-3.5" />
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={() => toggleBanAdmin(admin)}
            className={`h-8 w-8 p-0 ${admin.status === 'banned'
              ? 'border-green-500/30 text-green-400 hover:bg-green-500/10'
              : 'border-purple-500/30 text-purple-400 hover:bg-purple-500/10'}`}
            title={admin.status === 'banned'
              ? (language === 'ar' ? 'إلغاء الحظر' : 'Unban')
              : (language === 'ar' ? 'حظر/إيقاف' : 'Ban/Suspend')}
            data-testid={`admin-ban-${admin.id}`}
          >
            {admin.status === 'banned' ? <RotateCcw className="h-3.5 w-3.5" /> : <Ban className="h-3.5 w-3.5" />}
          </Button>
          <Button
            size="sm" variant="outline"
            onClick={() => canArchive && deleteAdmin(admin)}
            disabled={!canArchive}
            className={`h-8 w-8 p-0 ${canArchive
              ? 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10'
              : 'border-white/10 text-[#606060] cursor-not-allowed opacity-50'}`}
            title={canArchive
              ? (language === 'ar' ? 'أرشفة الحساب' : 'Archive account')
              : (language === 'ar' ? 'يجب إيقاف الحساب أولاً قبل الأرشفة' : 'Ban/Suspend the account first')}
            data-testid={`admin-archive-${admin.id}`}
          >
            <Archive className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AllAdminsSection({
  language,
  allAdmins,
  loadingAdmins,
  sendAdminResetLink,
  toggleBanAdmin,
  deleteAdmin,
  jobTitles = [],
  refreshAdmins,
}) {
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // 'all' | 'none' | job_title_id

  const filteredAdmins = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allAdmins.filter((a) => {
      if (roleFilter === 'none' && a.job_title_id) return false;
      if (roleFilter !== 'all' && roleFilter !== 'none' && a.job_title_id !== roleFilter) return false;
      if (!q) return true;
      return [a.name, a.email, a.username, a.employee_id, a.job_title, a.job_title_ar, a.phone]
        .some((v) => v && String(v).toLowerCase().includes(q));
    });
  }, [allAdmins, search, roleFilter]);

  return (
    <div
      className="bg-[#1a1a2e] rounded-3xl border border-[#2a2a4e] p-6"
      data-testid="all-admins-section"
    >
      <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
        <Users className="h-5 w-5 text-emerald-500" />
        {language === 'ar' ? 'حسابات المشرفين والموظفين' : 'Supervisors & Staff Accounts'}
        <span className="text-sm font-normal text-[#71717A]">
          ({filteredAdmins.length}{filteredAdmins.length !== allAdmins.length ? ` / ${allAdmins.length}` : ''})
        </span>
      </h2>

      {/* Search + Role filter */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-[#71717A] ${language === 'ar' ? 'right-3' : 'left-3'}`} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={language === 'ar' ? 'ابحث بالاسم، البريد، اليوزر، الرقم الوظيفي…' : 'Search by name, email, username, employee ID…'}
            className={`w-full h-10 rounded-xl bg-[#0a0a0a] border border-[#2a2a4e] text-white text-sm placeholder-[#606060] focus:outline-none focus:border-[#d4af37]/50 ${language === 'ar' ? 'pr-9 pl-3' : 'pl-9 pr-3'}`}
            data-testid="admins-search-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Briefcase className="h-4 w-4 text-[#71717A]" />
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="h-10 rounded-xl bg-[#0a0a0a] border border-[#2a2a4e] text-white text-sm px-3 focus:outline-none focus:border-[#d4af37]/50 min-w-[180px] [color-scheme:dark]"
            data-testid="admins-role-filter"
          >
            <option value="all" style={{ backgroundColor: '#0a0a0a', color: '#fff' }}>{language === 'ar' ? 'كل المسميات الوظيفية' : 'All job titles'}</option>
            <option value="none" style={{ backgroundColor: '#0a0a0a', color: '#fff' }}>{language === 'ar' ? 'بدون مسمى وظيفي' : 'No job title'}</option>
            {jobTitles.map((t) => (
              <option key={t.id || t._id} value={t.id || t._id} style={{ backgroundColor: '#0a0a0a', color: '#fff' }}>
                {language === 'ar' ? t.name_ar : (t.name_en || t.name_ar)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loadingAdmins ? (
        <div className="text-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-[#c9a227] mx-auto" />
          <p className="text-[#808080] mt-2">{language === 'ar' ? 'جاري التحميل...' : 'Loading...'}</p>
        </div>
      ) : filteredAdmins.length === 0 ? (
        <div className="text-center py-12 text-[#808080]">
          <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>
            {allAdmins.length === 0
              ? (language === 'ar' ? 'لا يوجد مشرفين' : 'No admins yet')
              : (language === 'ar' ? 'لا نتائج مطابقة للبحث/الفلتر' : 'No results matching search/filter')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredAdmins.map((admin) => (
            <AdminRow
              key={admin.id || admin.email}
              admin={admin}
              language={language}
              sendAdminResetLink={sendAdminResetLink}
              toggleBanAdmin={toggleBanAdmin}
              deleteAdmin={deleteAdmin}
            />
          ))}
        </div>
      )}
    </div>
  );
}
